/**
 * chat-post-processors.ts
 * 从 chat.ts 提取的 AI 响应后处理逻辑
 * 包括：记忆创建、定时提醒、朋友圈发布、好感度调整、表情包发送
 */

import type { ChatMessage, TimestampedSystemMessage } from '../types/chat'
import type { ScheduledTaskSpec } from '../types/scheduled-task'

import { markRaw } from 'vue'

import { formatDelayTime } from './chat-helpers'

// 浏览器/Node 的 setTimeout 最大可靠延迟约为 2^31-1 ms；长提醒需要分段等待，
// 否则超过约 24.8 天的延迟会溢出并提前触发。
const MAX_SAFE_TIMEOUT_MS = 2_147_000_000

function scheduleDelayedCallback(callback: () => void, delayMs: number): void {
  if (delayMs <= MAX_SAFE_TIMEOUT_MS) {
    setTimeout(callback, delayMs)
    return
  }
  setTimeout(() => scheduleDelayedCallback(callback, delayMs - MAX_SAFE_TIMEOUT_MS), MAX_SAFE_TIMEOUT_MS)
}

/**
 * AI 响应解析结果的类型（processAIResponse 的返回值子集）
 */
export interface ParsedAIResponse {
  text: string
  shouldCreateMemory?: boolean
  memoryContent?: string
  memoryKeywords?: string[]
  delaySeconds?: number
  reminderText?: string
  voice?: boolean
  post?: {
    type: string
    content: string
    imageDescription?: string
  }
  relationshipAdjustment?: {
    score_change: number
    reason: string
    new_type?: string
  }
  sticker?: {
    name: string
  }
  pluginActions?: Array<{
    pluginId: string
    action: string
    params?: Record<string, unknown>
  }>
  messages?: any[]
  recallTimestamp?: number
  offlineModeSwitch?: 'offline' | 'online'
  isSilent?: boolean
  patAction?: { senderId?: string }
  /** 角色内心独白（send_messages 的 inner_voice 字段，旧 JSON 旁路提取） */
  innerVoice?: string
  simulatedImages?: Array<{ description: string }>
  scheduledTask?: ScheduledTaskSpec
}

/**
 * 后处理所需的上下文（从 chat store 传入）
 */
export interface PostProcessContext {
  messages: { value: Array<ChatMessage | { role: string, content: unknown }> }
  /** 本次请求开始时锁定的联系人，避免异步 LLM 请求期间切换会话导致副作用落错位置。 */
  targetContactId?: string | null
  /** 将系统消息写入本次请求锁定的会话。 */
  pushMessageToTarget?: (message: ChatMessage | { role: string, content: unknown }) => void
  /** 获取本次请求锁定的会话消息，供延迟动作和上下文读取使用。 */
  getTargetMessages?: () => Array<ChatMessage | { role: string, content: unknown }>
  notifyMessagesChanged: () => void
  saveMessages: () => Promise<void>
  memoryStore: any
  contactsStore: any
  airiCardStore: any
  postsStore: any
  relationshipStore: any
  recallMessage?: (timestamp: number, contactId?: string | null) => boolean
}

/**
 * 将后处理产生的系统/动作消息写入本轮锁定的会话。
 *
 * 原生工具调用期间用户可能切换联系人；优先使用 chat.ts 注入的定向写入器，
 * 只有旧调用方没有提供时才回退到当前消息数组。
 */
function pushPostProcessMessage(ctx: PostProcessContext, message: any): void {
  if (ctx.pushMessageToTarget) {
    ctx.pushMessageToTarget(message)
    return
  }

  ctx.messages.value.push(message)
  ctx.notifyMessagesChanged()
}

function getLastUserMessage(messages: Array<ChatMessage | { role: string, content: unknown }>): ChatMessage | { role: string, content: unknown } | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === 'user')
      return messages[i]
  }
  return undefined
}

/**
 * 处理记忆创建
 */
export function handleMemoryCreation(parsed: ParsedAIResponse, ctx: PostProcessContext): boolean {
  const content = typeof parsed.memoryContent === 'string' ? parsed.memoryContent.trim() : ''
  const rawKeywords = parsed.memoryKeywords
  const keywords = rawKeywords === undefined
    ? []
    : Array.isArray(rawKeywords)
      ? rawKeywords.filter(keyword => typeof keyword === 'string' && keyword.trim()).map(keyword => keyword.trim()).slice(0, 20)
      : []
  if (!parsed.shouldCreateMemory || !content || content.length > 2000 || (rawKeywords !== undefined && (!Array.isArray(rawKeywords) || rawKeywords.some(keyword => typeof keyword !== 'string'))) || ctx.targetContactId === null)
    return false
  try {
    const created = ctx.memoryStore.createMemory(
      content,
      keywords,
      false,
      undefined,
      ctx.targetContactId ? { contactId: ctx.targetContactId } : undefined,
    )
    return Boolean(created)
  }
  catch (error) {
    console.warn('创建记忆失败，继续处理:', error)
    return false
  }
}

/**
 * 处理定时提醒
 */
export function handleReminder(parsed: ParsedAIResponse, ctx: PostProcessContext): boolean {
  if (typeof parsed.delaySeconds !== 'number' || !Number.isFinite(parsed.delaySeconds) || parsed.delaySeconds <= 0 || parsed.delaySeconds > 31_536_000)
    return false

  const delayMs = parsed.delaySeconds * 1000
  const reminderContent = typeof parsed.reminderText === 'string' && parsed.reminderText.trim()
    ? parsed.reminderText.trim().slice(0, 2000)
    : '时间到了！'
  const timeDisplay = formatDelayTime(parsed.delaySeconds)
  console.info(`⏰ 设置${timeDisplay}后的提醒: ${reminderContent}`)

  scheduleDelayedCallback(() => {
    // 显示提醒通知
    if ('Notification' in window && Notification.permission === 'granted') {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(reg =>
          reg.showNotification('MeowChat 提醒', {
            body: reminderContent,
            icon: '/logo.png',
          }),
        )
      }
      else {
        void new Notification('MeowChat 提醒', {
          body: reminderContent,
          icon: '/logo.png',
        })
      }
    }

    // 在聊天中添加提醒消息
    pushPostProcessMessage(ctx, {
      role: 'assistant',
      content: `⏰ ${reminderContent}`,
      slices: [{ type: 'text', text: `⏰ ${reminderContent}` }],
      tool_results: [],
      timestamp: Date.now(),
    })
    console.info('✅ 提醒已触发:', reminderContent)
  }, delayMs)
  return true
}

function formatScheduledTaskTiming(task: ScheduledTaskSpec): string {
  if (task.scheduleType === 'once') {
    if (task.delaySeconds)
      return `${Math.ceil(task.delaySeconds / 60)} 分钟后`
    return task.runAt || '稍后'
  }
  return `${task.scheduleType === 'daily' ? '每天' : '每周'} ${task.localTime || ''}`.trim()
}

/** 与 push-server 保持一致的定时任务入参校验，避免无效任务占用一次工具调用。 */
export function validateScheduledTask(task: Partial<ScheduledTaskSpec> | null | undefined): task is ScheduledTaskSpec {
  if (!task || (task.scheduleType !== 'once' && task.scheduleType !== 'daily' && task.scheduleType !== 'weekly'))
    return false
  if (typeof task.instruction !== 'string' || !task.instruction.trim() || task.instruction.trim().length > 2000)
    return false

  if (task.scheduleType === 'once') {
    const hasDelay = task.delaySeconds !== undefined
      && Number.isFinite(task.delaySeconds)
      && task.delaySeconds > 0
      && task.delaySeconds <= 366 * 86400
    const hasRunAt = typeof task.runAt === 'string'
      && /^\d{4}-\d{2}-\d{2}[T ](?:[01]\d|2[0-3]):[0-5]\d$/.test(task.runAt.trim())
    return hasDelay || hasRunAt
  }

  if (typeof task.localTime !== 'string' || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(task.localTime.trim()))
    return false
  if (task.scheduleType !== 'weekly')
    return true
  const weekday = task.weekday
  return typeof weekday === 'number' && Number.isInteger(weekday) && weekday >= 0 && weekday <= 6
}

/** 将原生工具或旧 JSON 中的 schedule_task 统一提交到服务端。 */
export async function handleScheduledTask(parsed: ParsedAIResponse, ctx: PostProcessContext): Promise<boolean> {
  if (!validateScheduledTask(parsed.scheduledTask))
    return false
  const contactId = ctx.targetContactId === undefined ? ctx.contactsStore.activeContact?.id : ctx.targetContactId
  const contact = ctx.targetContactId === undefined
    ? ctx.contactsStore.activeContact
    : (ctx.targetContactId ? ctx.contactsStore.getContact(ctx.targetContactId) : null)
  if (!contactId || contact?.isGroup)
    return false
  try {
    const { useOfflineSyncStore } = await import('./offlineSync')
    const offlineSyncStore = useOfflineSyncStore()
    const task = await offlineSyncStore.createScheduledTask({ contactId, ...parsed.scheduledTask })
    const confirmation = {
      role: 'system',
      content: `已安排定时任务：${formatScheduledTaskTiming(parsed.scheduledTask)}`,
      timestamp: Date.now(),
      metadata: { type: 'scheduled_task_created', taskId: task.id },
    } as any
    pushPostProcessMessage(ctx, confirmation)
    return true
  }
  catch (error) {
    console.warn('创建服务端定时任务失败:', error)
    return false
  }
}

/**
 * 处理朋友圈动态发布
 */
export function handlePostCreation(parsed: ParsedAIResponse, ctx: PostProcessContext): boolean {
  const post = parsed.post
  if (!post || (post.type !== 'text' && post.type !== 'image') || typeof post.content !== 'string' || !post.content.trim() || post.content.trim().length > 2000)
    return false
  if (post.type === 'image' && (typeof post.imageDescription !== 'string' || !post.imageDescription.trim() || post.imageDescription.trim().length > 2000))
    return false
  try {
    const activeContact = ctx.targetContactId !== undefined
      ? (ctx.targetContactId ? ctx.contactsStore.getContact(ctx.targetContactId) : null)
      : ctx.contactsStore.activeContact
    const characterName = activeContact?.name || ctx.airiCardStore.characterName || 'AI'
    const characterAvatar = activeContact?.avatar || '/BOT.jpg'
    const characterId = activeContact?.id || ctx.airiCardStore.activeCardId || 'default'

    ctx.postsStore.createPost({
      authorId: characterId,
      authorName: characterName,
      authorAvatar: characterAvatar,
      type: post.type,
      content: post.content.trim(),
      ...(post.type === 'image' && typeof post.imageDescription === 'string' ? { imageDescription: post.imageDescription.trim() } : {}),
    })
    console.info('📝 AI发布了朋友圈动态:', post.content.substring(0, 50))
    return true
  }
  catch (error) {
    console.warn('发布朋友圈失败，继续处理:', error)
    return false
  }
}

/**
 * 处理好感度调整
 */
export async function handleRelationshipAdjustment(
  parsed: ParsedAIResponse,
  ctx: PostProcessContext,
): Promise<boolean> {
  const adjustment = parsed.relationshipAdjustment
  if (!adjustment || typeof adjustment.score_change !== 'number' || !Number.isFinite(adjustment.score_change) || adjustment.score_change < -10 || adjustment.score_change > 10 || typeof adjustment.reason !== 'string' || !adjustment.reason.trim())
    return false
  try {
    const activeContact = ctx.targetContactId !== undefined
      ? (ctx.targetContactId ? ctx.contactsStore.getContact(ctx.targetContactId) : null)
      : ctx.contactsStore.activeContact
    const characterId = activeContact?.id || ctx.airiCardStore.activeCardId || 'default'
    const characterName = activeContact?.name || ctx.airiCardStore.characterName || 'AI'

    const { useRelationshipStore } = await import('../stores/relationship')
    const relationshipStore = useRelationshipStore()

    const lastUserMessage = getLastUserMessage(ctx.getTargetMessages?.() ?? ctx.messages.value)
    const userMessageContent = typeof lastUserMessage?.content === 'string'
      ? lastUserMessage.content
      : undefined

    const scoreChange = adjustment.score_change
    const rawNewType = adjustment.new_type
    const newType = rawNewType && typeof rawNewType === 'string'
      ? rawNewType.trim().slice(0, 20) || undefined
      : undefined

    // 更新好感度
    relationshipStore.updateAffection(
      characterId,
      characterName,
      scoreChange,
      adjustment.reason.trim().slice(0, 500),
      userMessageContent,
    )

    // 如果 AI 建议了新的关系类型，自动更新
    if (newType) {
      const relationship = relationshipStore.getRelationship(characterId)
      if (relationship) {
        // 冷却检查：距离上次关系类型变更不足 5 分钟时忽略
        const cooldownMs = 5 * 60 * 1000
        if (relationship.lastTypeChangeAt && Date.now() - relationship.lastTypeChangeAt < cooldownMs) {
          console.warn(`💕 关系类型变更冷却中，忽略 new_type: ${newType}`)
        }
        else {
          const oldTypeDisplay = relationshipStore.formatRelationshipType(relationship.type)
          relationshipStore.setRelationship(
            characterId,
            characterName,
            newType,
            relationship.affection,
            `关系变更: ${adjustment.reason || '对话互动'}`,
          )

          // 插入系统消息通知关系变化
          const newTypeDisplay = relationshipStore.formatRelationshipType(newType)
          const sysMsg: TimestampedSystemMessage = {
            role: 'system',
            content: `你和 ${characterName} 的关系变为了：${newTypeDisplay}`,
            timestamp: Date.now(),
            metadata: {
              type: 'relationship_change',
              oldType: oldTypeDisplay,
              newType: newTypeDisplay,
              characterName,
            },
          }
          pushPostProcessMessage(ctx, sysMsg)

          console.info(`💕 关系类型变更: ${characterName} ${oldTypeDisplay} → ${newTypeDisplay}`)
        }
      }
    }

    console.info(`💝 好感度调整: ${scoreChange > 0 ? '+' : ''}${scoreChange} - ${adjustment.reason}`)
    return true
  }
  catch (error) {
    console.warn('处理好感度调整失败，继续处理:', error)
    return false
  }
}

/**
 * 处理表情包发送
 */
export async function handleStickerSend(
  parsed: ParsedAIResponse,
  ctx: PostProcessContext,
): Promise<boolean> {
  if (!parsed.sticker?.name)
    return false
  try {
    const { useStickersStore } = await import('./stickers')
    const stickersStore = useStickersStore()

    // 获取当前联系人挂载的表情包组（不含用户人格的，用户人格的表情包仅供用户自己发送）
    const activeContactId = ctx.targetContactId === undefined ? ctx.contactsStore.activeContactId : ctx.targetContactId
    const contact = activeContactId ? ctx.contactsStore.contacts[activeContactId] : null
    const contactPackIds = contact?.stickerPackIds || []

    // 群聊时合并所有成员的表情包组
    const memberPackIds: string[] = []
    if (contact?.isGroup && contact.members) {
      for (const memberId of contact.members) {
        const member = ctx.contactsStore.contacts[memberId]
        if (member?.stickerPackIds) {
          memberPackIds.push(...member.stickerPackIds)
        }
      }
    }

    const allPackIds = [...new Set([...contactPackIds, ...memberPackIds])]
    const sticker = stickersStore.findStickerByNameInPacks(parsed.sticker.name, allPackIds)

    if (sticker) {
      const stickerContent = markRaw({
        type: 'sticker',
        url: sticker.url,
        name: sticker.name,
      })
      pushPostProcessMessage(ctx, {
        role: 'assistant',
        content: [stickerContent] as any,
        slices: [],
        tool_results: [],
        timestamp: Date.now(),
      })
      console.info(`😊 AI发送了表情包: ${sticker.name}`)
      return true
    }
    else {
      console.warn(`表情包"${parsed.sticker.name}" 未找到，已跳过`)
      return false
    }
  }
  catch (error) {
    console.warn('发送表情包失败，继续处理:', error)
    return false
  }
}

/**
 * 从消息内容中提取纯文本
 */
function getTextContent(content: any): string {
  if (typeof content === 'string')
    return content
  if (Array.isArray(content)) {
    return content
      .map((part: any) => {
        if (typeof part === 'string')
          return part
        if (part?.type === 'text')
          return part.text || ''
        return ''
      })
      .join('')
  }
  return ''
}

/**
 * 处理 AI 主动撤回消息
 * 当 recallTimestamp === -1 时，自动查找最近一条 AI 消息并撤回
 * 撤回成功后插入系统通知消息
 * 🆕 加入 2-5 秒随机延迟，模拟真人"发了又撤"的效果
 */
export function handleRecallMessage(parsed: ParsedAIResponse, ctx: PostProcessContext): void {
  if (parsed.recallTimestamp == null || !ctx.recallMessage)
    return
  try {
    let targetTimestamp = parsed.recallTimestamp

    // -1 表示自动查找最近的 AI 消息（跳过当前刚发送的回复）
    if (targetTimestamp === -1) {
      const allMessages = ctx.getTargetMessages?.() ?? ctx.messages.value
      let skippedFirst = false
      // 从后往前找，跳过第一条遇到的 assistant 消息（即当前回复），撤回第二条
      for (let i = allMessages.length - 1; i >= 0; i--) {
        const msg = allMessages[i] as any
        if (msg.role === 'assistant' && !msg.recalled && msg.timestamp) {
          if (!skippedFirst) {
            skippedFirst = true
            continue
          }
          targetTimestamp = msg.timestamp
          break
        }
      }
      if (targetTimestamp === -1) {
        console.warn('🔙 AI 撤回失败：找不到可撤回的 AI 消息')
        return
      }
    }

    // 在撤回前先获取消息内容（撤回后仍可访问，但提前取更安全）
    const getTargetMessages = ctx.getTargetMessages
    const targetMessages = getTargetMessages?.() ?? ctx.messages.value
    const targetMsg = targetMessages.find((m: any) => m.timestamp === targetTimestamp) as any
    const recalledContent = targetMsg ? getTextContent(targetMsg.content) : ''

    // 🆕 延迟 2-5 秒后再执行撤回，模拟真人"发了又撤"的效果
    // 让用户有机会看到原消息，增强"来不及撤回被看到"的真实感
    const delayMs = 2000 + Math.random() * 3000 // 2-5秒随机延迟
    console.info(`🔙 AI 将在 ${Math.round(delayMs / 1000)} 秒后撤回消息 (timestamp: ${targetTimestamp})`)

    const recallMessage = ctx.recallMessage
    const contactsStore = ctx.contactsStore

    setTimeout(() => {
      try {
        const success = recallMessage(targetTimestamp, ctx.targetContactId)
        if (success) {
          // 获取 AI 角色名
          const aiName = (ctx.targetContactId !== undefined
            ? (ctx.targetContactId ? contactsStore.getContact(ctx.targetContactId)?.name : undefined)
            : contactsStore.activeContact?.name) || 'AI'

          // 插入系统通知消息（与用户撤回时的逻辑一致）
          const systemMsg = {
            role: 'system',
            content: `${aiName}撤回了一条消息`,
            timestamp: Date.now(),
            metadata: {
              type: 'message_recalled',
              recalledContent: recalledContent.slice(0, 200),
              recalledAt: Date.now(),
            },
          } as any

          // 找到被撤回消息的位置，在其后插入系统消息
          const currentMessages = getTargetMessages?.() ?? targetMessages
          const msgIndex = currentMessages.findIndex((m: any) => m.timestamp === targetTimestamp)
          if (msgIndex !== -1) {
            currentMessages.splice(msgIndex + 1, 0, systemMsg)
          }
          else {
            currentMessages.push(systemMsg)
          }

          ctx.notifyMessagesChanged()
          console.info(`🔙 AI 主动撤回了消息 (timestamp: ${targetTimestamp})`)
        }
        else {
          console.warn(`🔙 AI 撤回失败，未找到对应消息 (timestamp: ${targetTimestamp})`)
        }
      }
      catch (error) {
        console.warn('AI 延迟撤回执行失败:', error)
      }
    }, delayMs)
  }
  catch (error) {
    console.warn('AI 撤回消息失败，继续处理:', error)
  }
}

/**
 * 处理 AI 主动切换线下模式
 */
export async function handleOfflineModeSwitch(
  parsed: ParsedAIResponse,
  ctx: PostProcessContext,
): Promise<void> {
  if (!parsed.offlineModeSwitch)
    return
  try {
    const { useOfflineMode } = await import('../composables/useOfflineMode')
    const { setOfflineModeByAI } = useOfflineMode()

    const characterName = (ctx.targetContactId !== undefined
      ? (ctx.targetContactId ? ctx.contactsStore.getContact(ctx.targetContactId)?.name : undefined)
      : ctx.contactsStore.activeContact?.name) || 'AI'
    const targetMessages = ctx.getTargetMessages?.() ?? ctx.messages.value
    setOfflineModeByAI(
      parsed.offlineModeSwitch,
      characterName,
      targetMessages,
      ctx.notifyMessagesChanged,
      ctx.targetContactId,
    )
    console.info(`🌙 AI主动切换线下模式: ${parsed.offlineModeSwitch}`)
  }
  catch (error) {
    console.warn('AI切换线下模式失败，继续处理:', error)
  }
}

export async function handlePluginActions(parsed: ParsedAIResponse, ctx: PostProcessContext): Promise<boolean> {
  if (!parsed.pluginActions || parsed.pluginActions.length === 0)
    return false

  let succeeded = true
  try {
    const { usePluginsStore } = await import('./plugins')
    const pluginsStore = usePluginsStore()
    const lastUserMessage = getLastUserMessage(ctx.getTargetMessages?.() ?? ctx.messages.value)
    const userInput = typeof lastUserMessage?.content === 'string' ? lastUserMessage.content : undefined

    for (const pluginAction of parsed.pluginActions) {
      try {
        const result = await pluginsStore.executeAIAction(
          pluginAction.pluginId,
          pluginAction.action,
          pluginAction.params || {},
          { userInput },
        )
        console.info(`🔧 AI执行插件动作成功: ${pluginAction.pluginId}.${pluginAction.action}`, result)
      }
      catch (error) {
        console.warn(`AI执行插件动作失败: ${pluginAction.pluginId}.${pluginAction.action}`, error)
        succeeded = false
      }
    }
  }
  catch (error) {
    console.warn('AI插件动作处理失败，继续处理:', error)
    return false
  }
  return succeeded
}

/**
 * 处理语音消息的 TTS 生成
 * 遍历已分发的消息，找 messageType === 'voice_message' 的，调用 TTS 生成音频
 *
 * @param parsed AI 响应解析结果，或 { forceRun: true } 强制绕过 guard（native tool path 使用）
 */
export async function handleVoiceMessageSend(
  parsed: ParsedAIResponse | { forceRun: true },
  ctx: PostProcessContext,
): Promise<void> {
  if (!('forceRun' in parsed) && (!parsed.messages || parsed.messages.length === 0))
    return

  const targetMessages = ctx.getTargetMessages?.() ?? ctx.messages.value
  const voiceMessages = targetMessages.filter(
    (m: any) => m.role === 'assistant' && m.messageType === 'voice_message' && !m.voiceProcessed,
  )

  if (voiceMessages.length === 0)
    return

  try {
    const { useSpeechStore } = await import('./modules/speech')
    const speechStore = useSpeechStore()

    const contactId = ctx.targetContactId === undefined ? ctx.contactsStore.activeContact?.id : ctx.targetContactId

    const { saveVoiceAudio, cleanupOldVoiceAudio } = await import('../utils/voice-audio-storage')

    for (const msg of voiceMessages) {
      const rawMsg = msg as any
      rawMsg.voiceProcessed = true

      const rawText = typeof rawMsg.content === 'string' ? rawMsg.content : ''
      rawMsg.voiceTranscript = rawText.replace(/\[[^\]]*\]/g, '').trim() || rawText

      // 只拦截 transcript 为空的情况；有无声音配置交给 generateSpeechForContact 判断
      // 联系人单独配置了声音时同样可以工作，不依赖全局 speechStore.configured
      if (!rawMsg.voiceTranscript) {
        rawMsg.voiceFallback = true
        rawMsg.voiceDuration = 2
        continue
      }

      try {
        rawMsg.voiceLoading = true
        ctx.notifyMessagesChanged()

        const audioBuffer = await speechStore.generateSpeechForContact(rawMsg.voiceTranscript, contactId)
        const messageId = rawMsg.timestamp ? String(rawMsg.timestamp) : `voice-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        const audioUrl = await saveVoiceAudio(messageId, audioBuffer, contactId || '')

        rawMsg.audioUrl = audioUrl
        rawMsg.voiceLoading = false
        ctx.notifyMessagesChanged()
        // 立即强制保存，防止防抖窗口内刷新导致 audioUrl 丢失
        await ctx.saveMessages()
      }
      catch (error) {
        console.warn('语音消息 TTS 生成失败，降级为文字语音条:', error)
        rawMsg.voiceFallback = true
        rawMsg.voiceLoading = false
        rawMsg.voiceDuration = Math.max(2, Math.ceil(rawMsg.voiceTranscript.length / 5))
        ctx.notifyMessagesChanged()
      }
    }

    // 异步清理旧音频，清理后同步清除消息里的 stale audioUrl
    cleanupOldVoiceAudio(75).then((deletedIds) => {
      if (deletedIds.length === 0)
        return
      const msgs = (ctx.getTargetMessages?.() ?? ctx.messages.value) as any[]
      let changed = false
      for (const msg of msgs) {
        if (msg.audioUrl && deletedIds.includes(String(msg.timestamp))) {
          delete msg.audioUrl
          changed = true
        }
      }
      if (changed) {
        ctx.notifyMessagesChanged()
        ctx.saveMessages()
      }
    }).catch(e => console.warn('语音音频清理失败:', e))
  }
  catch (error) {
    console.warn('语音消息后处理失败:', error)
  }
}

/**
 * 统一执行所有后处理（按顺序）
 */
export async function runAllPostProcessors(
  parsed: ParsedAIResponse,
  ctx: PostProcessContext,
): Promise<void> {
  handleRecallMessage(parsed, ctx)
  handleMemoryCreation(parsed, ctx)
  handleReminder(parsed, ctx)
  await handleScheduledTask(parsed, ctx)
  handlePostCreation(parsed, ctx)
  await handleRelationshipAdjustment(parsed, ctx)
  await handleStickerSend(parsed, ctx)
  await handleOfflineModeSwitch(parsed, ctx)
  await handlePluginActions(parsed, ctx)
  // handleVoiceMessageSend 需要在消息分发（dispatch）之后调用，由 chat.ts 单独调用
}
